import React, { useEffect, useMemo, useState } from "react";
import { Button } from "antd";
import { Form, InputGroup, Badge } from "react-bootstrap";
import { SaveOutlined } from "@ant-design/icons";
import { oneKlicks } from "../../../SDK/oneclick";
import { getuserdata } from "../../../services/apiService";
import { toast } from "react-toastify";

const Inventory = () => {
    const [search, setSearch] = useState("");
    const [list, setList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [savingId, setSavingId] = useState(null);

    const debounced = useMemo(() => {
        let t;
        return (val) => {
            clearTimeout(t);
            t = setTimeout(() => setSearch(val), 250);
        };
    }, []);

    const load = async () => {
        try {
            setLoading(true);
            const pharmacyId = await getuserdata("id");
            if (!pharmacyId) return;
            const res = await oneKlicks.read(
                "",
                "seller_pharmacy_products",
                { pharmacy_id: pharmacyId },
                "product_name ASC",
                search ? { product_name: search, brand: search, category: search } : {},
                { page: 1, limit: 100 }
            );
            setList(res?.success ? res.data || [] : []);
        } catch (e) {
            setList([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search]);

    const updateRow = async (row, changes) => {
        try {
            setSavingId(row.id);
            const pharmacyId = await getuserdata("id");
            const next = { ...row, ...changes };

            // Auto-derive stock_status from quantity if explicitly changed
            if (Object.prototype.hasOwnProperty.call(changes, "quantity")) {
                const qty = Number(changes.quantity || 0);
                if (qty <= 0) next.stock_status = "out_of_stock";
                else if (qty <= 10) next.stock_status = "low_stock";
                else next.stock_status = "in_stock";
            }

            const resp = await oneKlicks.update(
                "",
                "seller_pharmacy_products",
                {
                    quantity: Number(next.quantity || 0),
                    price: next.price,
                    discount: Number(next.discount || 0),
                    stock_status: next.stock_status,
                },
                { id: row.id, pharmacy_id: pharmacyId }
            );
            if (resp?.success) {
                toast.success("Inventory updated");
                await load();
            } else {
                toast.error(resp?.message || "Update failed");
            }
        } catch (e) {
            toast.error("Update failed");
        } finally {
            setSavingId(null);
        }
    };

    const getStockBadgeVariant = (status) => {
        switch (status?.toLowerCase()) {
            case "in_stock":
                return "success";
            case "low_stock":
                return "warning";
            case "out_of_stock":
                return "danger";
            default:
                return "secondary";
        }
    };

    const getStatusBadgeVariant = (status) => {
        switch (status?.toLowerCase()) {
            case "active":
                return "success";
            case "inactive":
                return "danger";
            default:
                return "secondary";
        }
    };

    return (
        <div className="container-fluid py-3">
            <div className="card mb-5">
                <div className="card-body">
                    <div className="align-items-center justify-content-between mb-3">
                        <div className="d-flex align-items-center gap-2 mb-2">
                            <h5 className="mb-0 font-weight-bold mr-3" style={{ color: 'white' }}>Manage Inventory / Stock</h5>
                            <Badge bg="info">Inventory</Badge>
                        </div>
                        <div className="d-flex justify-content-between align-items-center" style={{ gap: 12 }}>
                            <InputGroup className="flex-grow-1 me-2 h-40">
                                <Form.Control
                                    placeholder="Search by name, brand or category..."
                                    onChange={(e) => debounced(e.target.value)}
                                />
                            </InputGroup>
                        </div>
                    </div>
                </div>
            </div>
            <div className="card">
                <div className="card-body">
                    <div className="table-responsive">
                        <table hover size="sm" className="mb-0 align-middle">
                            <thead>
                                <tr>
                                    <th>Name</th>
                                    <th>Brand</th>
                                    <th>Category</th>
                                    <th style={{ width: 120 }}>Price</th>
                                    <th style={{ width: 120 }}>Quantity</th>
                                    <th>Stock Status</th>
                                    <th style={{ width: 120 }}>Discount %</th>
                                    <th>Status</th>
                                    <th style={{ width: 150 }}>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr>
                                        <td colSpan="9" className="text-center py-4">
                                            <div className="spinner-border text-primary" role="status">
                                                <span className="visually-hidden">Loading...</span>
                                            </div>
                                        </td>
                                    </tr>
                                ) : list.length === 0 ? (
                                    <tr>
                                        <td colSpan="9" className="text-center py-5">
                                            <h5 className="text-muted mb-2">No products found</h5>
                                            <p className="text-muted mb-3">
                                                Try adjusting your search criteria
                                            </p>
                                        </td>
                                    </tr>
                                ) : (
                                    list.map((row) => (
                                        <tr key={row.id}>
                                            <td data-label="Name" style={{ minWidth: 220 }}>
                                                <div style={{ fontWeight: '600', color: '#1890ff', marginBottom: '2px' }}>
                                                    {row.product_name}
                                                </div>
                                                <div style={{ fontSize: '12px', color: '#666' }}>{row.manufacturer || ""}</div>
                                            </td>
                                            <td data-label="Brand">{row.brand || "-"}</td>
                                            <td data-label="Category">
                                                <Badge bg="primary">{row.category || "-"}</Badge>
                                            </td>
                                            <td data-label="Price">
                                                <Form.Control
                                                    type="number"
                                                    min="0"
                                                    step="0.01"
                                                    defaultValue={row.price}
                                                    onChange={(e) => { row.price = e.target.value; }}
                                                    size="sm"
                                                />
                                            </td>
                                            <td data-label="Quantity">
                                                <Form.Control
                                                    type="number"
                                                    min="0"
                                                    defaultValue={row.quantity}
                                                    onChange={(e) => { row.quantity = Number(e.target.value); }}
                                                    size="sm"
                                                />
                                            </td>
                                            <td data-label="Stock Status">
                                                <Badge bg={getStockBadgeVariant(row.stock_status)}>
                                                    {row.stock_status?.replace("_", " ") || "-"}
                                                </Badge>
                                            </td>
                                            <td data-label="Discount %">
                                                <Form.Control
                                                    type="number"
                                                    min="0"
                                                    max="100"
                                                    defaultValue={row.discount || 0}
                                                    onChange={(e) => { row.discount = Number(e.target.value); }}
                                                    size="sm"
                                                />
                                            </td>
                                            <td data-label="Status">
                                                <Badge bg={getStatusBadgeVariant(row.status)}>
                                                    {row.status || "-"}
                                                </Badge>
                                            </td>
                                            <td data-label="Action">
                                                <Button
                                                    type="primary"
                                                    size="small"
                                                    icon={<SaveOutlined />}
                                                    disabled={savingId === row.id}
                                                    loading={savingId === row.id}
                                                    onClick={() => updateRow(row, { price: row.price, quantity: row.quantity, discount: row.discount })}
                                                    style={{ 
                                                        display: 'flex', 
                                                        alignItems: 'center', 
                                                        justifyContent: 'center',
                                                        minWidth: '80px'
                                                    }}
                                                >
                                                    {savingId === row.id ? "Saving..." : "Save"}
                                                </Button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Inventory;


